import {Column} from "@material-table/core";
import SearchSelect from "@ui-components/SearchSelect";
import React, {useContext, useState} from "react";
import apiRequest from "@services/apiRequest";
import CountryContext from "../CountryContext";
import AuthenticationService from "@services/AuthenticationService";
import {UserWithRoleSchema} from "../feature/types";
import {StylesConfig} from "react-select";
import {FormattedMessage} from "react-intl";
import {PRIVILEGED_ROLES} from "../constants";

type Option = {label: string; value: number};
type UserTableFilterProps<T extends object> = {
  type?: string;
  columnDef: Column<T>;
  onFilterChanged: (columnId: string, value: any) => void;
  limit?: number;
  onlyInteral?: boolean;
}

/**
 * This component is used as select on tables that needs
 * to fetch data from the backend.
 * It integrates with mu-tables events to refresh the table
 * based on selected filters.
 */
export const COMPONENT_STYLE_OVERRIDE: StylesConfig = {
  dropdownIndicator: (provided, state) => ({
    ...provided,
    padding: "2px 8px",
  }),
  clearIndicator: (provided, state) => ({
    ...provided,
    padding: "2px 8px",
  }),
  control: (provided, state) => ({
    ...provided,
    borderRadius: "1.3rem",
    borderColor: "transparent",
    boxShadow: "none !important",
    "&:hover": {
      borderColor: "transparent",
    },
    backgroundColor: "transparent",
    minHeight: "33px",
    cursor: "pointer",
  }),
  singleValue: (provided, state) => ({
    ...provided,
    position: "absolute",
  }),
  placeholder: (provided, state) => ({
    ...provided,
    position: "absolute",
  }),
  input: (provided, state) => ({
    input: {
      border: 0,
      outline: 0,
      boxShadow: "none !important",
    },
  }),
}

export const UserTableFilter = <T extends object, >({
  type,
  columnDef,
  onFilterChanged,
  limit=20,
  onlyInteral=false,
}: UserTableFilterProps<T>) => {

  // context
  const {country: {code}} = useContext(CountryContext);

  // state
  const [selected, setSelected] = useState<Option | null>(null);

  // utilities
  const {id, role_type} = AuthenticationService.getUserData();
  const perm = PRIVILEGED_ROLES.includes(role_type) ? role_type : `user_${id}`;
  const buildOption = (u: UserWithRoleSchema) => ({
    label: u.company ?? `${u.firstname} ${u.lastname}`,
    value: u.id,
  } as Option);
  
  return (
    // @ts-ignore
    <SearchSelect
      loadOptions={async (text: string, callback: (options: Option[]) => void) => {
        // --- build payload
        const filters = [] as {column: string, value: string, operator?:string}[];
        if (!!type)
          filters.push({column: "role", value: type});
        if (!!text)
          filters.push({column: "full_name", value: `%${text}%`});
        if (onlyInteral)
          filters.push({column: "flg_internal", operator:'=', value: "true"});
        const payload = {
          country_code: code,
          perm,
          filters: filters,
          ordering: {column: "full_name", orderDirection: "asc"},
          limit,
          offset: 0,
          include_internal: true,
          include_admins: true,
        };
        // --- fetch data
        const result: {items: UserWithRoleSchema[], total: number} = await apiRequest.post("/connections", payload);
        callback(result.items.map(buildOption));
      }}
      onChange={(selected: {label: string, value: number} | null) => {
        onFilterChanged((columnDef as any).tableData.id as string, selected?.value);
        setSelected(selected);
      }}
      value={selected}
      isMulti={false}
      mainStyle="border-2 border-kerakoll-400 mt-1 mb-0"
      componentStyle={COMPONENT_STYLE_OVERRIDE as any}
      noOptionsMessage={() => <FormattedMessage id="type_to_search" />}
      isClearable={true}
    />
  )
}